/**
 * DigitalEvent Video Chat Async actions
 */
import awsRestAPI from "@/services/api/awsRestAPI";
import dayjs from "dayjs";

import {
  ANNOUNCEMENT_TYPES,
  INVITE_STATUSES,
  MEDIA_DEVICE_STATUSES,
  TABLE_TYPES,
  VIDEO_CHAT_TYPES,
  VIDEO_MEMBER_ROLES,
  VIDEO_MEMBER_TYPES,
  VIDEO_SESSION_SIGNAL_TYPES,
  WSS_ACTION_TYPES
} from "@/helpers/constants";

export default {
  /** Toggle Video Chats functionality */
  async toggleVideoChatsFunctionality({ dispatch, commit, rootGetters }, videoChatEnabled) {
    videoChatEnabled =
      videoChatEnabled !== undefined ? videoChatEnabled : rootGetters["room/activeRoom"].VIDEO_CHATS_ENABLED;

    if (videoChatEnabled) {
      await dispatch("calls/devices/checkSystemSupported", null, { root: true });
      await dispatch("calls/devices/checkVideoDeviceAvailable", null, { root: true });
      if (rootGetters["calls/devices/isSystemSupported"] && rootGetters["users/currentUserPermissions"].canVideoChat) {
        await dispatch("calls/devices/getAvailableSources", null, { root: true });
      }
    } else {
      await dispatch("leaveVideoCall");
      await dispatch("updateUserInfo", { mediaDeviceStatus: MEDIA_DEVICE_STATUSES.DENY });
      commit("setVideoChatMembers", []);
      commit("setVideoChats", []);
    }
  },

  /**
   * Get access to the user's media devices (webcam and mic)
   * @param state {Object}
   * @return {Promise<void>}
   */
  async getMediaDevices({ rootGetters }) {
    if (rootGetters["tables/userTable"].TABLE_TYPE === TABLE_TYPES.PUBLIC_GALLERY) {
      // TODO: move this, public does not need device permissions
    }
  },

  /**
   * Get list of user's video chats
   * @param commit {Function}
   * @return {Promise<*>}
   */
  async getVideoChats({ commit }) {
    let response = await awsRestAPI.getVideoChats();

    commit("setVideoChats", response.data.video_chats);

    return response.data;
  },

  /**
   * Get or create video chat
   * @param state {Object}
   * @param getters {Object}
   * @param dispatch {Function}
   * @param recipientUUID {String}
   * @return {Promise<void>}
   */
  async getOrCreateVideoChat({ state, getters, dispatch, rootGetters, rootState }, recipientUUID) {
    if (state.activeVideoChat) {
      return state.activeVideoChat;
    }

    let videoChat = null;

    if (rootGetters["invites/sentVideoCallInvites"].length) {
      videoChat = getters.videoChat(rootGetters["invites/sentVideoCallInvites"][0].groupUUID);
    } else if (recipientUUID) {
      videoChat = getters.videoChatByRecipientUUID(recipientUUID);
    }

    if (!videoChat) {
      let data = await dispatch("createVideoChat", { recipientUUID: recipientUUID || null });
      videoChat = data.video_chat;
    } else {
      // Init video chat: set initiatorUUID, allowInvitations to False and update sessionId
      await dispatch("updateVideoChat", {
        videoChatUUID: videoChat.groupUUID,
        data: {
          allowInvitations: false,
          initiatorUUID: rootState.users.user.user_uuid,
          sessionId: null,
          role: VIDEO_MEMBER_ROLES.MODERATOR
        }
      });
    }

    return videoChat;
  },

  /**
   * Create 1-1 Video chat
   * @param state {Object}
   * @param commit {Function}
   * @param params {Object}
   * @return {Promise<*>}
   */
  async createVideoChat({ commit }, params) {
    let response = await awsRestAPI.createVideoChat(params);

    commit("addVideoChat", response.data.video_chat);

    return response.data;
  },

  /**
   * Update video chat - set title, icon
   * @param commit {Function}
   * @param dispatch {Function}
   * @param videoChatUUID {String}
   * @param data {Object}
   * @return {Promise<*>}
   */
  async updateVideoChat({ commit, dispatch }, { videoChatUUID, data }) {
    let response = await awsRestAPI.updateVideoChat(videoChatUUID, data);

    commit("updateVideoChat", {
      videoChatUUID: videoChatUUID,
      data: response.data.video_chat
    });

    await dispatch(
      "socket/sendSystemMessage",
      {
        groupUUID: videoChatUUID,
        messageActionType: WSS_ACTION_TYPES.VIDEO_CHAT_UPDATED,
        messageBody: {
          videoChatUUID: videoChatUUID,
          data: data
        }
      },
      { root: true }
    );

    return response.data;
  },

  /**
   * Delete video chat
   * @param commit {Function}
   * @param videoChatUUID {String}
   * @return {Promise<*>}
   */
  async deleteVideoChat({ commit }, videoChatUUID) {
    let response = await awsRestAPI.deleteVideoChat(videoChatUUID);

    commit("deleteVideoChat", videoChatUUID);

    return response.data;
  },

  /**
   * Leave video chat
   * @param commit {Function}
   * @param videoChatUUID {String}
   * @return {Promise<*>}
   */
  async leaveVideoChat({ commit }, { videoChatUUID }) {
    let response = await awsRestAPI.leaveVideoChat(videoChatUUID);

    commit("deleteVideoChat", videoChatUUID);

    return response.data;
  },

  /**
   * Join to the video call
   * @param state {Object}
   * @param commit {Function}
   * @param getters {Object}
   * @param videoChatUUID {String}
   * @param recipientUUID {String}
   * @return {Promise<*>}
   */
  async joinVideoCall({ state, commit, getters }, { videoChatUUID, recipientUUID }) {
    if (state.activeVideoChat) {
      return;
    }

    let videoChat = getters.videoChat(videoChatUUID);

    let response = await awsRestAPI.joinVideoCall(videoChatUUID, {
      recipientUUID: recipientUUID || null
    });

    if (videoChat) {
      commit("updateVideoChat", {
        videoChatUUID: videoChatUUID,
        data: response.data.video_chat
      });
    } else {
      commit("addVideoChat", response.data.video_chat);
    }

    commit("setActiveVideoChat", getters.videoChat(response.data.video_chat.groupUUID));

    return response.data;
  },

  /**
   * Leave video call
   * @param state {Object}
   * @param commit {Function}
   * @param dispatch {Function}
   * @return {Promise<*>}
   */
  async leaveVideoCall({ state, commit, dispatch }) {
    if (!state.activeVideoChat) {
      return;
    }

    let videoChatUUID = state.activeVideoChat.groupUUID;

    // Show special banner for the last two users
    if (state.connectionsCount <= 2 && state.oneToOneChatParticipantFullName) {
      commit(
        "socket/showAnnounce",
        {
          message: {
            startedAt: state.activeVideoChat.startedAt,
            participantFullName: state.oneToOneChatParticipantFullName
          },
          announceType: ANNOUNCEMENT_TYPES.ONE_TO_ONE_VIDEO_CALL_ENDED
        },
        { root: true }
      );
    }

    await dispatch("invites/cancelVideoCallInvites", null, { root: true });
    await dispatch("closeActiveVideoSession");

    let response = await awsRestAPI.leaveVideoCall(videoChatUUID);

    return response.data;
  },

  /**
   * Process connection created
   * @param commit {Function}
   * @param connection {Object}
   * @return {Promise<void>}
   */
  async processConnectionCreated({ commit }, { connection }) {
    commit("setVideoChatMember", { userUUID: connection.data, data: { connection: connection } });
  },

  /**
   * Process and Add Video Stream to the store
   * @param getters {object}
   * @param state {Object}
   * @param commit {Function}
   * @param stream {Object}
   * @param dispatch {Function}
   * @return {Promise<void>}
   */
  async addVideoChatStream({ state, commit, dispatch, rootGetters }, stream) {
    let invite = rootGetters["invites/sentVideoCallInvites"].find(
      invite => invite.inviteeUUID === stream.connection.data
    );
    if (invite) {
      commit("invites/deleteSentInvite", invite, { root: true });
    }

    let existingStream = !!state.streams.find(s => s.connection.data === stream.connection.data);
    if (!existingStream) {
      commit("addStream", stream);
      commit("incrementConnectionsCount");
      commit("setVideoChatMember", {
        userUUID: stream.connection.data,
        data: {
          memberType: VIDEO_MEMBER_TYPES.SUBSCRIBER,
          publisher: null,
          invite: null,
          stream: stream
        }
      });
    }

    if (state.connectionsCount === 2) {
      let participant = rootGetters["users/getUser"](state.activeVideoChat.recipientUUID);
      commit("setParticipantFullName", rootGetters["users/getUserFullName"](participant));
    } else if (state.connectionsCount > 2 && state.activeVideoChat.videoChatType === VIDEO_CHAT_TYPES.ONE_TO_ONE) {
      commit("updateVideoChat", {
        videoChatUUID: state.activeVideoChat.groupUUID,
        data: {
          videoChatType: VIDEO_CHAT_TYPES.MANY_TO_MANY
        }
      });
    }

    await dispatch("logVideoChatsAction", {
      actionName: "STREAM_CREATED",
      userUUID: stream.connection.data
    });
  },

  /**
   * Process and Delete stream from the store
   * @param commit {Function}
   * @param state {Object}
   * @param dispatch {Function}
   * @param getters {Object}
   * @param stream {Object}
   * @return {Promise<void>}
   */
  async deleteVideoChatStream({ commit, state, dispatch, rootState, rootGetters }, stream) {
    let existingStream = !!state.streams.find(s => s.connection.data === stream.connection.data);
    if (!existingStream) {
      return;
    }

    let userUUID = stream.connection.data;

    if (state.connectionsCount === 3) {
      let member = state.videoChatMembers.find(member => {
        return member.userUUID !== rootState.users.user.user_uuid && member.userUUID !== userUUID;
      });
      if (member) {
        let participant = rootGetters["users/getUser"](member.userUUID);
        commit("setParticipantFullName", rootGetters["users/getUserFullName"](participant));
      }
    }

    commit("deleteStream", stream);
    commit("deleteVideoChatMember", userUUID);
    commit("decrementConnectionsCount");

    if (state.connectionsCount === 1) {
      await dispatch("leaveVideoCall");
    }

    await dispatch("logVideoChatsAction", {
      actionName: "STREAM_DESTROYED",
      userUUID: userUUID
    });
  },

  /**
   * Log video chats action
   * @param getters {Object}
   * @param state {Object}
   * @param actionName {String}
   * @param userUUID {String}
   * @param error {Object}
   */
  async logVideoChatsAction({ getters, state }, { actionName, userUUID, error }) {
    if (getters.videoChatConfig.showVideoChatsLogs) {
      let now = `[${dayjs(new Date()).format("HH:mm:ss")}]`;
      let action = `[VIDEO_CHATS][${actionName}]`;
      let videoChat = state.activeVideoChat ? "[CHAT::ACTIVE]" : "[CHAT::INACTIVE]";
      let videoSession = state.activeVideoSession ? "[SESSION::ACTIVE]" : "[SESSION::INACTIVE]";
      let connectionsCount = `[CONNECTIONS_COUNT::${state.connectionsCount}]`;
      let streamsCount = `[STREAMS_COUNT::${state.streams.length}]`;
      let subscribersCount = `[SUBSCRIBERS_COUNT::${state.subscribers.length}]`;
      userUUID = userUUID ? `[USER_UUID::${userUUID}]` : "";

      // eslint-disable-next-line no-console
      console.log(
        `${now}${action}${videoChat}${videoSession}${connectionsCount}${streamsCount}${subscribersCount}${userUUID}`
      );

      if (error) {
        // eslint-disable-next-line no-console
        console.error(`${action}`, error);
      }
    }
  },

  /** Close active video session */
  async closeActiveVideoSession({ state, commit, dispatch, rootState }) {
    if (state.activeVideoSession) {
      state.activeVideoSession.unpublish(rootState.calls.defaultPublisher);
      await state.subscribers.forEach(subscriber => {
        if (subscriber.stream) {
          state.activeVideoSession.unsubscribe(subscriber);
        }
      });
      await state.activeVideoSession.destroy();
      commit("setActiveVideoSession", null);
    }

    dispatch("processPublisher", null);
    commit("deleteVideoChatMembers");
    commit("deleteStreams");
    commit("deleteSubscribers");
    commit("setConnectionsCount", 0);
    commit("setVideoCallInitiator", null);
    commit("setActiveVideoChat", null);
  },

  /**
   * Process publisher info
   * @param state {Object}
   * @param commit {Function}
   * @param value {Object|null}
   */
  processPublisher({ commit, rootState }, value) {
    commit("setVideoChatMember", {
      userUUID: rootState.users.user.user_uuid,
      data: {
        memberType: value ? VIDEO_MEMBER_TYPES.PUBLISHER : VIDEO_MEMBER_TYPES.SELF,
        publisher: value || null,
        invite: null,
        stream: null,
        activity: null
      }
    });
  },

  /**
   * Add user to video call if it doesn't already exist
   * @param state {Object}
   * @param commit {Function}
   * @param userUUID {String}
   */
  addUserInfoToVideoCall({ state, commit }, userUUID) {
    if (!state.videoChatMembers.find(member => member.userUUID === userUUID)) {
      commit("setVideoChatMember", {
        userUUID: userUUID,
        data: {
          memberType: VIDEO_MEMBER_TYPES.INVITEE,
          publisher: null,
          invite: {
            inviteStatus: INVITE_STATUSES.ACCEPT
          },
          stream: null
        }
      });
    }
  },

  /**
   * Process Video session signal from server SDK
   * @param getters {Object}
   * @param commit {Function}
   * @param dispatch {Function}
   * @param type {String}
   * @param data {String}
   */
  processSessionSignal({ getters, commit, dispatch, rootGetters }, { type, data }) {
    let user = rootGetters["users/getUser"](data);

    if (type === `signal:${VIDEO_SESSION_SIGNAL_TYPES.LEAVE_GROUP_VIDEO_CALL}`) {
      commit("deleteVideoChatMember", user.userUUID);

      commit(
        "socket/showAnnounce",
        {
          message: {
            messageBody: {
              participantFullName: rootGetters["users/getUserFullName"](user)
            }
          },
          announceType: ANNOUNCEMENT_TYPES.LEAVE_GROUP_VIDEO_CALL
        },
        { root: true }
      );

      if (data === getters.activeVideoChat.initiatorUUID) {
        commit("updateVideoChat", {
          videoChatUUID: getters.activeVideoChat.groupUUID,
          data: { allowInvitations: true }
        });
      }
    } else if (type === `signal:${VIDEO_SESSION_SIGNAL_TYPES.JOIN_GROUP_VIDEO_CALL}`) {
      dispatch("addUserInfoToVideoCall", data);
    }

    dispatch("logVideoChatsAction", {
      actionName: `GET_SIGNAL::${type}::${data}`
    });
  },

  /**
   * Update video chat member audio level
   * @param getters {Object}
   * @param commit {Function}
   * @param event {Object}
   */
  updateVideoChatMemberAudioLevel({ getters, commit }, event) {
    if (!event.target.stream) {
      return;
    }

    let userUUID = event.target.stream.connection.data;
    let videoChatMember = getters.videoChatMember(userUUID);

    if (!videoChatMember) {
      return;
    }

    let activity = videoChatMember.activity ? Object.assign({}, videoChatMember.activity) : null;
    let now = Date.now();

    if (event.audioLevel > 0.1) {
      if (!activity) {
        activity = { timestamp: now, talking: false };
      } else if (activity && activity.talking) {
        activity.timestamp = now;
      } else if (activity && now - activity.timestamp > (getters.videoChatConfig.startTalkingFrom || 1000)) {
        activity.talking = true;
      }
      commit("setVideoChatMember", {
        userUUID: userUUID,
        data: { activity: activity }
      });
    } else if (activity && now - activity.timestamp > (getters.videoChatConfig.stopTalkingAfter || 3000)) {
      commit("setVideoChatMember", {
        userUUID: userUUID,
        data: { activity: null }
      });
    }
  },

  /**
   * Update user's info in Dynamo DB
   * @param stateObjects {Object}
   * @param data {Object}
   * @return {Promise<*>}
   */
  async updateUserInfo(stateObjects, data) {
    let response = await awsRestAPI.updateUserInfo(data);

    return response.data;
  }
};
